import { useState, useRef, useEffect, useMemo } from "react";
import {
  useLoaderData,
  useFetcher,
  useOutletContext,
  Outlet,
  useParams,
  useSearchParams,
  Link,
  useTransition,
  useLocation,
} from "@remix-run/react";

import {
  AdjustmentsHorizontalIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import * as yup from "yup";
import { ValidatedForm } from "remix-validated-form";
import { withYup } from "@remix-validated-form/with-yup";
import { json } from "@remix-run/node";
import {
  classNames,
  GetSearchBody,
  reorder,
  getURLsearchParamsFromState,
} from "~/utils";
import { sortProfessionalArticleFilterData } from "~/utils/facharticle_search_helper";
import apiClient from "~/api/axios.server";
import { useInView } from "react-intersection-observer";
import type { ISpecialistArticlesResponse } from "~/types";
import { SearchBox } from "~/shared";
import { Breadcrumb } from "~/screens";
import { Slideover } from "~/components";
import { ErrorBoundaryLayoutComponent, Spinner, Tabs, Modal } from "~/shared";
import theme from "~/theme/default";
import { WeiterFilter, MainFilter } from "~/screens";

import type { currentTabProp } from "~/shared";
import type {
  ResultLIStItem,
  StateFieldsValue,
} from "~/screens/search_filter/types";
import { GetScreenSize } from "~/utils";
import { useMatchesData } from "~/useMatchesData";

const validator = withYup(
  yup.object({
    curriculumIds: yup.array().nullable(),
    secondaryEducationIds: yup.array().nullable(),
    academicYearIds: yup.array().nullable(),
    subjectAreaIds: yup.array().nullable(),
    mediaKindIds: yup.array().nullable(),
    subjectIds: yup.array().nullable(),
    extentIds: yup.array().nullable(),
    licenseIds: yup.array().nullable(),
    mediaCompetenceIds: yup.array().nullable(),
    educationalApplicationAreaIds: yup.array().nullable(),
    specialIds: yup.array().nullable(),
    authorIds: yup.array().nullable(),
    tagIds: yup.array().nullable(),
    q: yup.array().nullable(),
    sort: yup.array().nullable(),
    inclusions: yup.array().nullable(),
  })
);

const tabs = [
  {
    name: "Medien",
    status: "default",
    id: 2,
    url: "/search/main",
    isInternal: true,
  },
  {
    name: "Fachbeiträge",
    status: "default",
    id: 1,
  },
  {
    name: "Veranstaltungen",
    status: "inprogress",
    id: 3,
    url: "https://veranstaltungen.ru-digital.de",
  },
];

export const loader = async ({ request, params }: any) => {
  if (params.articleId) {
    return {};
  }
  const url = new URL(request.url);
  const cliend = apiClient();
  let sortQuery = url.searchParams.get("sort");
  let sort = sortQuery === "desc" ? "desc" : "asc";
  const page = url.searchParams.get("page") || 1;

  const searchBody = GetSearchBody(request);

  try {
    const { data } = await cliend.get<ISpecialistArticlesResponse>(
      //@ts-ignore
      `/specialist_articles?page=${page}&per_page=${40}&sort=${sort}`,
      {
        data: searchBody,
      }
    );

    const {
      curriculumIds,
      secondaryEducationIds,
      academicYearIds,
      learningUnitIds,
      subjectAreaIds,
      q,
      ...withoutMainFilterData
    } = data.metadata.searchAttributes;
    const {
      mediaKindIds,
      educationalApplicationAreaIds,
      mediaCompetenceIds,
      restrictionPresenceIds,
      thematicFocusIds,
      inclusionIds,
      extentIds,
      specialIds,
      licenseIds,
      authorIds,
      tagIds,
      subjectIds,
      q: query,
      ...withoutWeiterFilterData
    } = data.metadata.searchAttributes;
    const weiterFilterCount = Object.values(withoutMainFilterData).flat(1);
    const mainFilterCount = Object.values(withoutWeiterFilterData).flat(1);

    return json(
      {
        resultList: data?.specialistArticles,
        currentPage: data?.metadata.currentPage,
        totalPages: data?.metadata.totalPages,
        searchToken: data?.metadata.searchToken,
        totalEntries: data?.metadata.totalEntries,
        perPage: data?.metadata.perPage,
        searchAttributes: data?.metadata.searchAttributes,
        weiterFilterCount: weiterFilterCount.length,
        mainFilterCount: mainFilterCount.length,
        sort: sort,
        hasNextPage: data?.metadata.currentPage < data?.metadata.totalPages,
      },
      {
        headers: {
          "Cache-Control": "public, max-age=30, s-maxage=86400",
        },
      }
    );
  } catch (error: any) {
    throw new Error(String(error.message));
  }
};

export const ErrorBoundary = ({ error }: any) => {
  return <ErrorBoundaryLayoutComponent error={error} />;
};

export default function ProfessionalArticleMain() {
  const location = useLocation();
  const matchdata = useMatchesData("/", location.pathname);
  const [searchParams] = useSearchParams();
  const authors = matchdata?.authors;
  const tagsList = matchdata?.tagsList;
  const mainFilterData = matchdata?.mainFilterData;
  const filterData = matchdata?.filterData;
  // Use custom filter data for professional articles (excludes specific filters)
  const weiterFilterData = filterData
    ? sortProfessionalArticleFilterData(filterData)
    : [];
  const defaultValuesMainForm = useMemo(
    () => matchdata?.defaultValuesMainForm || {},
    [matchdata?.defaultValuesMainForm]
  );

  //STATE
  const {
    resultList,
    currentPage,
    totalPages,
    totalEntries,
    perPage,
    searchToken,
    weiterFilterCount,
    searchAttributes,
    mainFilterCount,
    sort,
    hasNextPage,
  } = useLoaderData<ResultLIStItem>();

  const [weiterFilterOpen, setWeiterFilter] = useState(false);
  const [MainFilteropen, setMainFilteropen] = useState(
    false || searchParams.get("openmoldal") === "1"
  );
  //@ts-ignore
  const [items, setItems] = useState<ResultLIStItem>({});

  const [current, setCurrent] = useState<currentTabProp>({
    status: "unassigned",
    id: 1,
  });
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [query, setSearchQuery] = useState(
    searchParams && searchParams.getAll("q").length
      ? searchParams.getAll("q")[0]
      : ""
  );
  const [fieldValue, setFieldValue] = useState<StateFieldsValue>(
    defaultValuesMainForm
  );

  //HOOKS
  const { articleId } = useParams();
  const transition = useTransition();
  const fetcher = useFetcher();
  const size = GetScreenSize();

  const isFetchingNextPage =
    fetcher.state === "loading" && resultList?.length > 39;
  const { showPendingSpinner } =
    useOutletContext<{ showPendingSpinner: boolean }>();

  //REFS
  const page = useRef(1);
  const formRef = useRef(null);
  const { button } = theme;
  const { base, navBar, color } = button;

  const { url } = getURLsearchParamsFromState({
    fieldValue,
    query,
    currentPage,
  });

  const formAction = `/professional_article/main?${url}`;
  const isStateItemshasNextPage = items?.currentPage < items?.totalPages;
  const isSearchedOrFilteredResultHasNexxtPage = currentPage < totalPages;

  const { ref, entry } = useInView({
    onChange: (inView, entery) => {
      if (
        !inView ||
        (fetcher && isFetchingNextPage && articleId) ||
        !isSearchedOrFilteredResultHasNexxtPage ||
        !isStateItemshasNextPage
      ) {
        return;
      }

      fetchNextPage(entery);
    },
    threshold: 0,
  });

  const isIntersecting = entry?.isIntersecting && resultList?.length > 39;

  const [sortedData] = reorder({
    stateData: items?.resultList,
    serverData: resultList,
    mainFilterCount,
    weiterFilterCount,
    hasMore: isStateItemshasNextPage,
    transitionState: transition.state,
    size,
    query,
  });

  useEffect(() => {
    const handleSetData = () => {
      setItems({
        resultList,
        currentPage,
        totalPages,
        totalEntries,
        perPage,
        searchToken,
        weiterFilterCount,
        searchAttributes,
        mainFilterCount,
        defaultValuesMainForm,
        sort,
        hasNextPage,
      });
    };
    if (resultList?.length > 39 && totalEntries !== items?.totalEntries) {
      handleSetData();
    }
  }, [
    currentPage,
    defaultValuesMainForm,
    isSearchedOrFilteredResultHasNexxtPage,
    items?.totalEntries,
    mainFilterCount,
    perPage,
    resultList,
    searchAttributes,
    searchToken,
    sort,
    totalEntries,
    totalPages,
    weiterFilterCount,
    hasNextPage,
  ]);

  useEffect(() => {
    const handleSetData = () => {
      setItems(
        ({
          resultList: stateResult,
          weiterFilterCount,
          searchAttributes,
          mainFilterCount,
          defaultValuesMainForm,
          sort,
        }) => {
          return {
            resultList: [...stateResult, ...fetcher?.data?.resultList],
            currentPage: fetcher?.data?.currentPage,
            totalEntries: fetcher?.data?.totalEntries,
            totalPages: fetcher?.data?.totalPages,
            perPage: fetcher?.data?.perPage,
            searchToken: fetcher?.data?.searchToken,
            weiterFilterCount,
            searchAttributes,
            mainFilterCount,
            defaultValuesMainForm,
            sort,
            hasNextPage: fetcher?.data?.currentPage < fetcher?.data?.totalPages,
          };
        }
      );
    };
    if (
      fetcher?.data &&
      isIntersecting &&
      fetcher?.data?.currentPage === page?.current
    ) {
      handleSetData();
    }
  }, [isIntersecting, fetcher?.data, hasNextPage]);

  useEffect(() => {
    if (transition.state === "loading") {
      page.current = 1;
      setWeiterFilter(false);
      setMainFilteropen(false);
    }
  }, [transition.state]);

  useEffect(() => {
    if (transition.state === "submitting") {
      page.current = 1;
      setMainFilteropen(false);
      setWeiterFilter(false);
    }
  }, [transition.state]);

  const fetchNextPage = (entery: any) => {
    if (entery.isIntersecting) {
      // TEMPORARY FIX: The API returns limited results for authors (first 20) and tags.
      // Same workaround as media search - preserve authorIds and tagIds from URL
      // See doc/known-issues.md for details
      const stateParams = new URLSearchParams(url);
      const urlParams = new URLSearchParams(location.search);

      // Preserve authorIds and tagIds from URL if they exist (handles IDs not in initial list)
      const urlAuthorIds = urlParams.get("authorIds");
      if (urlAuthorIds) {
        stateParams.set("authorIds", urlAuthorIds);
      }

      const urlTagIds = urlParams.get("tagIds");
      if (urlTagIds) {
        stateParams.set("tagIds", urlTagIds);
      }

      stateParams.set("page", String((page.current += 1)));
      fetcher.load(`/professional_article/main?${stateParams.toString()}`);
    }
  };
  const dataui = {
    data: sortedData,
    showPendingSpinner,
    isFetchingNextPage,
    isStateItemshasNextPage,
  };
  const handleSetSearchFieldValue = (
    e: any,
    fieldValue: { name: string; id: number }
  ) => {
    const { name, checked, id } = e.target;

    setFieldValue((prev: any) => {
      let idKeyValue = id.includes("subjectAreaIds") ? id.split("-")[0] : id;
      let selectedValues = prev[idKeyValue] ? prev[idKeyValue] : [];

      if (checked) {
        let prevState = {
          ...prev,
          [idKeyValue]: [
            ...selectedValues,
            {
              value: fieldValue.id,
              checked: checked,
              name: name,
              id: fieldValue.id,
              title: fieldValue.name,
            },
          ],
        };
        return prevState;
      } else {
        let itemWIthOutCheckedITem = prev[idKeyValue].filter((item: any) =>
          item ? item.id !== fieldValue.id : []
        );

        let prevState = {
          ...prev,
          [idKeyValue]: [...itemWIthOutCheckedITem],
        };

        return prevState;
      }
    });
  };

  const handleResetFilters = (filterName: "weiterFilter" | "main") => {
    const mainFilterValues = [
      "secondaryEducationIds",
      "curriculumIds",
      "academicYearIds",
      "subjectAreaIds",
    ];
    setFieldValue((prev: StateFieldsValue) => {
      let newState = { ...prev };
      for (const key in newState) {
        if (filterName === "main" && mainFilterValues.includes(key)) {
          delete newState[key];
        }
        if (filterName === "weiterFilter" && !mainFilterValues.includes(key)) {
          delete newState[key];
        }
      }
      return newState;
    });
  };

  return (
    <ValidatedForm
      formRef={formRef}
      className="pb-10"
      validator={validator}
      method="get"
      defaultValues={defaultValuesMainForm}
    >
      <div id="main_filter">
        <div className="section-wrapper">
          {!articleId ? (
            <div>
              <div className="mt-4 md:pb-4 md:pt-4">
                <Tabs
                  tabs={tabs}
                  onSelect={setCurrent}
                  current={current.id}
                  counter={0}
                />
              </div>

              <SearchBox
                formAction={formAction}
                query={query}
                setSearchQuery={setSearchQuery}
                isLandingPage={false}
              />

              <div className="flex w-full flex-col justify-between lg:mt-4 lg:flex-row lg:items-center">
                <div className="order-last mt-4 flex md:mt-0 lg:order-first lg:block lg:border-0">
                  <span className="ml-2 font-bold text-textColor-light md:ml-0">
                    {totalEntries} Fachartikel
                  </span>
                </div>

                <div className="flex w-full flex-row md:w-auto md:justify-end">
                  <button
                    type="button"
                    onClick={() => setMainFilteropen(!MainFilteropen)}
                    className={classNames(
                      "group relative flex w-full px-5 text-xs font-normal",
                      base,
                      navBar
                    )}
                  >
                    {" "}
                    <AdjustmentsHorizontalIcon
                      className={classNames(
                        "-ml-0.5 mr-2 h-3 w-3 md:h-5 md:w-5"
                      )}
                      aria-hidden="true"
                    />
                    <span>Lehrplanfilter</span>
                    {mainFilterCount > 0 && (
                      <span className="absolute top-0 right-0 block h-3 w-3 rounded-full bg-red-400 ring-2 ring-white" />
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={() => setWeiterFilter(!weiterFilterOpen)}
                    className={classNames(
                      "group relative w-full px-5 text-xs font-normal text-primary lg:my-0 lg:ml-3",
                      base,
                      navBar
                    )}
                  >
                    <span>Weitere Filter</span>
                    {weiterFilterCount > 0 && (
                      <span className="absolute top-0 right-0 block h-3 w-3 rounded-full bg-red-400 ring-2 ring-white" />
                    )}
                  </button>

                  <Slideover
                    open={weiterFilterOpen}
                    toggle={setWeiterFilter}
                    size={size}
                  >
                    <div className="opacity-1 flex h-full w-full flex-col divide-y divide-gray-200 rounded-r-3xl bg-white pt-2 shadow-xl blur-none">
                      <div className="flex min-h-0 flex-1 flex-col overflow-y-scroll py-6">
                        <div className="px-4 sm:px-6">
                          <div className="flex items-start justify-between">
                            <div className="text-lg font-bold text-gray-900">
                              Fachartikel filtern
                            </div>
                            <div className="ml-3 flex h-7 items-center">
                              <button
                                type="button"
                                className="rounded-full bg-primary text-white focus:outline-none focus:ring-2 focus:ring-btnColor-secondary"
                                onClick={() => setWeiterFilter(false)}
                              >
                                <span className="sr-only">Close panel</span>
                                <XMarkIcon
                                  className="h-6 w-6"
                                  aria-hidden="true"
                                />
                              </button>
                            </div>
                          </div>
                        </div>
                        <div className="relative mt-6 flex-1 px-4 sm:px-6">
                          <WeiterFilter
                            setFieldValue={handleSetSearchFieldValue}
                            fieldsValue={fieldValue}
                            tagsList={tagsList}
                            authorList={authors}
                            //@ts-ignore
                            filterData={weiterFilterData}
                          />
                        </div>
                      </div>
                      <div className="flex justify-between px-4 py-4">
                        <button
                          type="button"
                          onClick={() => handleResetFilters("weiterFilter")}
                          className={classNames(
                            base,
                            "rounded-none border-b border-b-textColor-primary pb-1 text-sm hover:border-b-btnColor-secondary"
                          )}
                        >
                          Alle Filter löschen
                        </button>
                        <Link
                          to={formAction}
                          className={classNames(
                            "px-6 text-base font-bold text-white",
                            base,
                            color.secondary
                          )}
                        >
                          Speichern
                        </Link>
                      </div>
                    </div>
                  </Slideover>
                </div>
              </div>
            </div>
          ) : (
            <div className="mt-5">
              {" "}
              <Breadcrumb mainPage="Fachartikel" detailsPage="Fachbeiträge" />
            </div>
          )}
        </div>

        <Modal
          open={MainFilteropen}
          setOpen={setMainFilteropen}
          title="Fachartikel nach Lehrplänen finden"
        >
          <MainFilter
            mobileFiltersOpen={mobileFiltersOpen}
            setMobileFiltersOpen={setMobileFiltersOpen}
            action={formAction}
            setOpen={setMainFilteropen}
            fieldsValue={fieldValue}
            setFieldValue={handleSetSearchFieldValue}
            filterData={mainFilterData}
            handleResetMainFilter={handleResetFilters}
          />
        </Modal>

        <Outlet context={dataui} />

        <div className="mt-2 mb-2 flex w-full justify-center p-3">
          <button
            className="whitespace-nowrap text-right text-sm text-gray-700"
            //@ts-ignore
            ref={ref}
            disabled={!isStateItemshasNextPage || isFetchingNextPage}
          >
            {isFetchingNextPage && !articleId ? <Spinner /> : null}
          </button>
        </div>
      </div>
    </ValidatedForm>
  );
}
